/**
 * Query keys, cache policy and the hooks the application calls.
 *
 * TanStack Query's defaults are tuned for a world where every failure is worth
 * retrying and every window focus is worth a refetch. Neither is true here: a
 * 401 means the session ended and retrying it three times just delays the
 * sign-in redirect by a second and a half, and a 403 will be a 403 again.
 * `retryPolicy` encodes that once.
 */

import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import * as api from './operations';
import { ApiError } from './problem';

/**
 * Every cache key in the application, built from one tree.
 *
 * A flat list of string literals scattered through the codebase is how an
 * invalidation ends up missing the key it was meant to clear. These are
 * hierarchical, so invalidating `keys.org(orgId)` clears everything below it.
 */
export const keys = {
  session: ['session'] as const,
  currentUser: ['session', 'user'] as const,
  tokens: ['session', 'tokens'] as const,
  mfa: ['session', 'mfa'] as const,
  myOrganizations: ['session', 'organizations'] as const,

  org: (orgId: string) => ['org', orgId] as const,
  organization: (orgId: string) => ['org', orgId, 'detail'] as const,
  favorites: (orgId: string) => ['org', orgId, 'favorites'] as const,
  watching: (orgId: string) => ['org', orgId, 'watching'] as const,
  activity: (orgId: string) => ['org', orgId, 'activity'] as const,
  notifications: (orgId: string, unread?: boolean) =>
    ['org', orgId, 'notifications', { unread: unread ?? null }] as const,

  repository: (orgId: string, repoId: string) => ['org', orgId, 'repository', repoId] as const,
  securitySummary: (orgId: string, repoId: string) =>
    ['org', orgId, 'repository', repoId, 'security-summary'] as const,
};

/**
 * Retry a request only when retrying could plausibly produce a different answer.
 *
 * - A transport failure might be a flaky network: retry.
 * - 408, 429 and 5xx are the server saying "not now": retry.
 * - 401, 403, 404, 409 and 422 are the server saying "no": do not.
 *
 * Three attempts, because the fourth has never been the one that worked and the
 * user is waiting for all of them.
 */
export function retryPolicy(failureCount: number, error: unknown): boolean {
  if (failureCount >= 3) return false;
  if (!(error instanceof ApiError)) return false;
  if (error.isTransport) return true;
  const { status } = error;
  return status === 408 || status === 429 || status >= 500;
}

/**
 * Honour `Retry-After` when the server sent one, and back off otherwise.
 *
 * A 429 from this API carries `Retry-After` (the rate-limit responses in the
 * contract all do), and ignoring it in favour of a fixed backoff is how a
 * client that is already being throttled makes it worse.
 */
export function retryDelay(attempt: number, error: unknown): number {
  if (error instanceof ApiError && error.retryAfterSeconds !== undefined) {
    return Math.min(error.retryAfterSeconds * 1000, 30_000);
  }
  return Math.min(500 * 2 ** attempt, 8_000);
}

/**
 * Assert the invariant `enabled` already encodes.
 *
 * Every organization-scoped hook here is written as `enabled: Boolean(orgId)`
 * with a `queryFn` that uses `orgId`, and TanStack cannot express to
 * TypeScript that the second never runs when the first is false. The usual
 * patches are both bad: `orgId!` asserts something the compiler cannot check
 * (and `strictTypeChecked` forbids the assertion *and* demands it, through two
 * rules that disagree), while `orgId ?? ''` sends an empty string into a URL
 * path and turns a programming error into a confusing 404.
 *
 * This throws instead, from inside the query function, where TanStack catches
 * it and surfaces it as the query's error. The invariant is stated once and
 * the failure names which one broke.
 */
function requireScope(value: string | undefined, name: string): string {
  if (value === undefined || value === '') {
    throw new Error(
      `${name} is required for this query. It is guarded by \`enabled\`, so reaching here means a hook ran without it.`,
    );
  }
  return value;
}

/* ====================================================================== *
 * Session
 * ====================================================================== */

/**
 * The three states a visitor can be in.
 *
 * Modelled as data rather than as an error, because two of the three are
 * ordinary. A consumer that has to read `error.code` to discover that nobody is
 * signed in will eventually forget to, and the failure mode is a red banner
 * where a sign-in form belonged.
 *
 * `mfa_required` is a state and not a failure for the same reason, and because
 * `internal/api/auth.go` makes it one: a session in that state can reach
 * exactly two routes, so it is not "signed in and something went wrong", it is
 * a distinct position in the flow with its own screen.
 */
export type SessionState =
  | { status: 'anonymous' }
  | { status: 'mfa_required' }
  | { status: 'authenticated'; user: api.User };

/**
 * Who is signed in, and how far through signing in they are.
 *
 * Only a genuine failure rejects — a 500, a timeout, the API being down. That
 * distinction is what lets the shell show "Exi Git is unreachable" instead of
 * bouncing the user to a sign-in form that will also fail.
 */
export function useSession(): UseQueryResult<SessionState, ApiError> {
  return useQuery<SessionState, ApiError>({
    queryKey: keys.currentUser,
    queryFn: async (): Promise<SessionState> => {
      try {
        return { status: 'authenticated', user: await api.getCurrentUser() };
      } catch (error) {
        if (error instanceof ApiError) {
          if (error.status === 401) return { status: 'anonymous' };
          if (error.code === 'mfa_required') return { status: 'mfa_required' };
        }
        throw error;
      }
    },
    // The session does not change under us often, and every route guard reads
    // this. A short stale time here is a request on every navigation.
    staleTime: 60_000,
    // One retry, not three.
    //
    // `retryPolicy` is right for a query that fails inside an already-rendered
    // page: the user is reading something while it retries. This query blocks
    // the entire interface — nothing can render until it answers — and three
    // retries with backoff is nearly four seconds of an unexplained skeleton
    // before the "Exi Git is not responding" panel appears. Measured at 3.8s
    // against a dead API. One retry still absorbs a single blip and gets the
    // explanation on screen in about a second.
    retry: (failureCount, error) => failureCount < 1 && retryPolicy(failureCount, error),
  });
}

export function useOrganization(
  orgId: string | undefined,
): UseQueryResult<api.Organization, ApiError> {
  return useQuery<api.Organization, ApiError>({
    queryKey: keys.organization(orgId ?? ''),
    queryFn: () => api.getOrganization(requireScope(orgId, 'orgId')),
    enabled: Boolean(orgId),
    staleTime: 5 * 60_000,
    retry: retryPolicy,
  });
}

/* ====================================================================== *
 * The personal surface
 * ====================================================================== */

export function useFavoriteRepositories(
  orgId: string | undefined,
): UseQueryResult<api.Page<api.Repository>, ApiError> {
  return useQuery<api.Page<api.Repository>, ApiError>({
    queryKey: keys.favorites(orgId ?? ''),
    queryFn: () => api.listFavoriteRepositories(requireScope(orgId, 'orgId')),
    enabled: Boolean(orgId),
    staleTime: 30_000,
    retry: retryPolicy,
  });
}

export function useWatchedRepositories(
  orgId: string | undefined,
): UseQueryResult<api.Page<api.Repository>, ApiError> {
  return useQuery<api.Page<api.Repository>, ApiError>({
    queryKey: keys.watching(orgId ?? ''),
    queryFn: () => api.listWatchedRepositories(requireScope(orgId, 'orgId')),
    enabled: Boolean(orgId),
    staleTime: 30_000,
    retry: retryPolicy,
  });
}

export function usePersonalActivity(
  orgId: string | undefined,
  limit = 20,
): UseQueryResult<api.Page<api.ActivityEntry>, ApiError> {
  return useQuery<api.Page<api.ActivityEntry>, ApiError>({
    queryKey: [...keys.activity(orgId ?? ''), limit],
    queryFn: () => api.listPersonalActivity(requireScope(orgId, 'orgId'), { limit }),
    enabled: Boolean(orgId),
    staleTime: 15_000,
    retry: retryPolicy,
  });
}

export function useNotifications(
  orgId: string | undefined,
  options: { unread?: boolean; limit?: number } = {},
): UseQueryResult<api.Page<api.Notification>, ApiError> {
  return useQuery<api.Page<api.Notification>, ApiError>({
    queryKey: [...keys.notifications(orgId ?? '', options.unread), options.limit ?? 30],
    queryFn: () =>
      api.listNotifications(requireScope(orgId, 'orgId'), {
        ...(options.unread === undefined ? {} : { unread: options.unread }),
        limit: options.limit ?? 30,
      }),
    enabled: Boolean(orgId),
    staleTime: 15_000,
    retry: retryPolicy,
  });
}

export function useAcknowledgeNotification(
  orgId: string | undefined,
): UseMutationResult<api.Notification, ApiError, string> {
  const queryClient = useQueryClient();
  return useMutation<api.Notification, ApiError, string>({
    mutationFn: (notificationId: string) =>
      api.acknowledgeNotification(requireScope(orgId, 'orgId'), notificationId),
    onSuccess: () => {
      // Both the unread and the all-notifications lists, hence the prefix.
      void queryClient.invalidateQueries({ queryKey: ['org', orgId ?? '', 'notifications'] });
    },
  });
}

export function useSecuritySummary(
  orgId: string | undefined,
  repoId: string | undefined,
): UseQueryResult<api.SecuritySummary, ApiError> {
  return useQuery<api.SecuritySummary, ApiError>({
    queryKey: keys.securitySummary(orgId ?? '', repoId ?? ''),
    queryFn: () =>
      api.getSecuritySummary(requireScope(orgId, 'orgId'), requireScope(repoId, 'repoId')),
    enabled: Boolean(orgId) && Boolean(repoId),
    staleTime: 30_000,
    retry: retryPolicy,
  });
}

export interface RepositoryPosture {
  repository: api.Repository;
  summary: api.SecuritySummary | undefined;
  isPending: boolean;
  error: ApiError | null;
}

/**
 * One security summary per repository, fetched in parallel.
 *
 * There is no bulk endpoint — `GET …/repositories/{repo_id}/security/summary`
 * is per repository and there is no organization-wide roll-up — so this is N
 * requests, and the caller is expected to pass a bounded list. The Overview
 * passes the first eight favourites rather than every repository the user
 * watches, because a command center that fires forty requests on load is a
 * command center people stop opening.
 *
 * A failure on one repository is kept beside that repository rather than
 * failing the set. A user with eight repositories and one they have lost
 * access to should see seven summaries and one explained gap.
 */
export function useRepositoryPostures(
  orgId: string | undefined,
  repositories: api.Repository[],
): RepositoryPosture[] {
  const results = useQueries({
    queries: repositories.map((repository) => ({
      queryKey: keys.securitySummary(orgId ?? '', repository.id),
      queryFn: () => api.getSecuritySummary(requireScope(orgId, 'orgId'), repository.id),
      enabled: Boolean(orgId),
      staleTime: 60_000,
      retry: retryPolicy,
    })),
  });

  return repositories.map((repository, index) => {
    const result = results[index];
    return {
      repository,
      summary: result?.data,
      isPending: result?.isPending ?? true,
      error: (result?.error as ApiError | null | undefined) ?? null,
    };
  });
}

export function useSecurityPolicy(
  orgId: string | undefined,
): UseQueryResult<api.SecurityPolicy | null, ApiError> {
  return useQuery<api.SecurityPolicy | null, ApiError>({
    queryKey: [...keys.org(orgId ?? ''), 'security-policy'],
    queryFn: () => api.getSecurityPolicy(requireScope(orgId, 'orgId')),
    enabled: Boolean(orgId),
    staleTime: 5 * 60_000,
    retry: retryPolicy,
  });
}

export function useTokens(): UseQueryResult<api.Page<api.Token>, ApiError> {
  return useQuery<api.Page<api.Token>, ApiError>({
    queryKey: keys.tokens,
    queryFn: () => api.listTokens(),
    staleTime: 60_000,
    retry: retryPolicy,
  });
}

export function useUserOrganizations(): UseQueryResult<api.Page<api.UserOrganization>, ApiError> {
  return useQuery<api.Page<api.UserOrganization>, ApiError>({
    queryKey: keys.myOrganizations,
    queryFn: () => api.listMyOrganizations(),
    staleTime: 5 * 60_000,
    retry: retryPolicy,
  });
}
